// useDealTitleOrUnavailable — общий резолв «название сделки или признак недоступности»
// для всех мест рендера «Сделка …» (TasksListPage / TaskDetailPage / EventDetailPage;
// рефакторинг §6.2 №2 — D4 Data Clumps). Канон FIX-C: retry=false — 404 удалённой
// сделки (софт-делит, контракт backend DealService.getById) детерминирован и не ретраится.

import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { dealsApi } from '@/api/deals';

/** staleTime резолва сделок (канон FIX-C TasksListPage: сделки меняются редко). */
const DEAL_TITLE_STALE_MS = 5 * 60 * 1000;

/** Результат резолва одной сделки для рендера «Сделка …». */
export interface DealTitleResolution {
  /** Название сделки, если запрос успешно завершён. */
  readonly title: string | undefined;
  /** Сделка недоступна (404 — soft-delete; отображается «Сделка удалена», некликабельно). */
  readonly unavailable: boolean;
}

/**
 * Резолвит сделки поштучно (GET /api/deals/{id} — batch-аналога на backend нет) и
 * возвращает карту id → {@link DealTitleResolution}. До завершения запроса у id
 * записи нет (title=undefined, unavailable=false) — вызывающий рендерит «…».
 *
 * @param dealIds id сделок видимых строк (дубликаты схлопываются)
 * @returns карта «id сделки → резолв»
 */
export function useDealTitleOrUnavailable(dealIds: readonly string[]): Map<string, DealTitleResolution> {
  const uniqueIds = useMemo(() => [...new Set(dealIds)], [dealIds]);
  const dealQueries = useQueries({
    queries: uniqueIds.map((dealId) => ({
      queryKey: ['deal', dealId],
      queryFn: () => dealsApi.get(dealId),
      staleTime: DEAL_TITLE_STALE_MS,
      retry: false,
    })),
  });
  return useMemo(() => {
    const resolutions = new Map<string, DealTitleResolution>();
    uniqueIds.forEach((dealId, index) => {
      const query = dealQueries[index];
      resolutions.set(dealId, {
        title: query?.data?.title,
        unavailable: Boolean(query?.isError),
      });
    });
    return resolutions;
  }, [dealQueries, uniqueIds]);
}
